// Beyond AI — Report Writer. Drafts structured UK H&S / building-safety reports. Phase 7.
import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Wand2, Loader2, Copy, Download, FileText, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import Markdown from "@/components/Markdown";

export default function ReportWriterPage() {
    const [meta, setMeta] = useState({ report_types: [] });
    const [clients, setClients] = useState([]);
    const [buildings, setBuildings] = useState([]);
    const [reports, setReports] = useState([]);
    const [form, setForm] = useState({ report_type: "", client_id: "", building_id: "", title: "", notes: "" });
    const [busy, setBusy] = useState(false);
    const [active, setActive] = useState(null);
    const [denied, setDenied] = useState(false);
    const [pulling, setPulling] = useState(false);
    const [params] = useSearchParams();
    const projectId = params.get("project") || "";
    const prefilledRef = useRef(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const loadReports = useCallback(async () => {
        try {
            const { data } = await api.get("/ai/reports");
            setReports(data);
            setDenied(false);
        } catch (e) { if (e.response?.status === 403) setDenied(true); }
    }, []);

    useEffect(() => {
        api.get("/ai/meta").then((r) => setMeta(r.data)).catch(() => {});
        api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
        api.get("/buildings").then((r) => setBuildings(r.data)).catch(() => {});
        loadReports();
    }, [loadReports]);

    const filteredBuildings = buildings.filter((b) => !form.client_id || b.client_id === form.client_id);

    const pullCtx = useCallback(async (ctx, opts = {}) => {
        setPulling(true);
        try {
            const { data } = await api.get("/ai/context-summary", { params: ctx });
            setForm((f) => ({ ...f, notes: (f.notes ? f.notes.trim() + "\n\n" : "") + data.text }));
            if (!opts.silent) toast.success(`Pulled live data for ${data.label}`);
            return data;
        } catch (e) {
            if (!opts.silent) toast.error(formatApiError(e.response?.data?.detail) || "Could not pull data");
            return null;
        } finally { setPulling(false); }
    }, []);

    // Pre-fill from a project (one-click "Generate report" off a project page)
    useEffect(() => {
        if (!projectId || prefilledRef.current) return;
        prefilledRef.current = true;
        const client = params.get("client") || "";
        const building = params.get("building") || "";
        const title = params.get("title") || "";
        setForm((f) => ({
            ...f,
            client_id: client || f.client_id,
            building_id: building || f.building_id,
            title: title || f.title,
            report_type: f.report_type || "Building Safety Case Summary",
        }));
        pullCtx({ context_type: "project", context_id: projectId }, { silent: true });
    }, [projectId, params, pullCtx]);

    async function generate() {
        if (!form.report_type) { toast.error("Choose a report type"); return; }
        setBusy(true);
        setActive(null);
        try {
            const payload = { report_type: form.report_type, notes: form.notes };
            if (form.client_id) payload.client_id = form.client_id;
            if (form.building_id) payload.building_id = form.building_id;
            if (form.title) payload.title = form.title;
            const { data } = await api.post("/ai/report", payload);
            setActive(data);
            loadReports();
            toast.success("Draft ready");
        } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Generation failed"); }
        finally { setBusy(false); }
    }

    async function pullData() {
        const ctx = projectId
            ? { context_type: "project", context_id: projectId }
            : form.building_id
                ? { context_type: "building", context_id: form.building_id }
                : form.client_id
                    ? { context_type: "client", context_id: form.client_id }
                    : null;
        if (!ctx) { toast.error("Select a client or building first"); return; }
        await pullCtx(ctx);
    }

    function copy() {
        if (!active) return;
        navigator.clipboard.writeText(active.content);
        toast.success("Copied to clipboard");
    }

    function download() {
        if (!active) return;
        const blob = new Blob([active.content], { type: "text/markdown" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${(active.title || "report").replace(/[^a-z0-9]+/gi, "-")}.md`;
        a.click();
        URL.revokeObjectURL(url);
    }

    async function removeReport(id, e) {
        e.stopPropagation();
        if (!window.confirm("Delete this draft?")) return;
        try {
            await api.delete(`/ai/reports/${id}`);
            if (active?.id === id) setActive(null);
            loadReports();
        } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    }

    if (denied) {
        return (
            <div data-testid="ai-report-denied" className="bg-white border rounded-[11px] p-8 text-center" style={{ borderColor: "#E5E0DA" }}>
                <p className="text-[14px] font-semibold text-[#1F1F1F]">You don't have access to the AI report writer.</p>
            </div>
        );
    }

    return (
        <div data-testid="ai-report-page" className="space-y-5">
            <div>
                <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase" style={{ borderBottom: "2px solid #1F6FE5", color: "#6F6A64" }}>Beyond AI</span>
                <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F] flex items-center gap-2"><Wand2 className="w-5 h-5" style={{ color: "#1F6FE5" }} /> AI Report Writer</h2>
                <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>Turn rough site notes into a structured, professional report draft.</p>
            </div>

            <div className="grid lg:grid-cols-[360px_1fr] gap-4">
                {/* Form + history */}
                <div className="space-y-4">
                    <div className="bg-white border rounded-[14px] p-4 space-y-3" style={{ borderColor: "#E1E8F2" }}>
                        <Field label="Report type" required>
                            <select data-testid="ai-report-type" value={form.report_type} onChange={(e) => set("report_type", e.target.value)} className="ai-fi">
                                <option value="">Select…</option>
                                {meta.report_types.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Client (optional)">
                            <select data-testid="ai-report-client" value={form.client_id} onChange={(e) => { set("client_id", e.target.value); set("building_id", ""); }} className="ai-fi">
                                <option value="">—</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Building / site (optional)">
                            <select data-testid="ai-report-building" value={form.building_id} onChange={(e) => set("building_id", e.target.value)} className="ai-fi">
                                <option value="">—</option>
                                {filteredBuildings.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Title (optional)">
                            <input data-testid="ai-report-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="ai-fi" placeholder="Auto-generated if blank" />
                        </Field>
                        <Field label="Notes / findings">
                            <button type="button" data-testid="ai-report-pull-data" onClick={pullData} disabled={pulling || (!form.client_id && !form.building_id && !projectId)}
                                className="mb-1.5 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#EEF3FB] text-[#1F6FE5] text-[11px] font-semibold hover:bg-[#E0ECFB] disabled:opacity-50">
                                {pulling ? <Loader2 className="w-3 h-3 animate-spin" /> : <Wand2 className="w-3 h-3" />} Pull live data from selected record
                            </button>
                            <textarea data-testid="ai-report-notes" rows={6} value={form.notes} onChange={(e) => set("notes", e.target.value)} className="ai-fi" placeholder="Paste your site notes, observations and key findings…" />
                        </Field>
                        <button data-testid="ai-report-generate" onClick={generate} disabled={busy}
                            className="w-full inline-flex items-center justify-center gap-1.5 py-2.5 rounded-[9px] text-white bg-ai-grad text-[12.5px] font-semibold disabled:opacity-60">
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}{busy ? "Drafting…" : "Generate draft"}
                        </button>
                    </div>

                    <div className="bg-white border rounded-[14px] p-3" style={{ borderColor: "#E1E8F2" }}>
                        <div className="text-[10.5px] font-bold uppercase tracking-wider px-1 pb-2" style={{ color: "#9A968F" }}>Recent drafts</div>
                        <div className="space-y-1" data-testid="ai-report-history">
                            {reports.length === 0 && <div className="text-[11.5px] italic px-1 py-2" style={{ color: "#9A968F" }}>No drafts yet.</div>}
                            {reports.map((r) => (
                                <div key={r.id} data-testid={`ai-report-item-${r.id}`} onClick={() => setActive(r)}
                                    className={`group flex items-center gap-2 px-2.5 py-2 rounded-[8px] cursor-pointer text-[12px] ${active?.id === r.id ? "bg-[#EEF3FB] font-semibold" : "hover:bg-[#F6F8FB]"}`}>
                                    <FileText className="w-3.5 h-3.5 shrink-0" style={{ color: "#1F6FE5" }} />
                                    <span className="flex-1 truncate text-[#1F1F1F]">{r.title}</span>
                                    <button onClick={(e) => removeReport(r.id, e)} className="opacity-0 group-hover:opacity-100 text-[#B33A2E]"><Trash2 className="w-3.5 h-3.5" /></button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Output */}
                <div className="bg-white border rounded-[14px] min-h-[400px] flex flex-col" style={{ borderColor: "#E1E8F2" }}>
                    {active ? (
                        <>
                            <div className="px-5 py-3 border-b flex items-center justify-between gap-3" style={{ borderColor: "#EEF3FB" }}>
                                <div className="min-w-0">
                                    <div className="text-[14px] font-bold text-[#1F1F1F] truncate">{active.title}</div>
                                    <div className="text-[10.5px] flex items-center gap-1" style={{ color: "#6F6A64" }}><Clock className="w-3 h-3" />{active.report_type}</div>
                                </div>
                                <div className="flex items-center gap-2 shrink-0">
                                    <button data-testid="ai-report-copy" onClick={copy} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-[8px] border text-[11.5px] font-semibold bg-white" style={{ borderColor: "#E1E8F2" }}><Copy className="w-3.5 h-3.5" /> Copy</button>
                                    <button data-testid="ai-report-download" onClick={download} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-[8px] text-white bg-ai-grad text-[11.5px] font-semibold"><Download className="w-3.5 h-3.5" /> .md</button>
                                </div>
                            </div>
                            <div className="px-6 py-4 overflow-y-auto flex-1" data-testid="ai-report-output"><Markdown text={active.content} /></div>
                        </>
                    ) : (
                        <div className="flex-1 grid place-items-center text-center px-6">
                            <div>
                                <div className="w-14 h-14 rounded-2xl bg-ai-grad-soft grid place-items-center mb-3 mx-auto" style={{ border: "1px solid #BFD8F5" }}><Wand2 className="w-7 h-7" style={{ color: "#1F6FE5" }} /></div>
                                <div className="text-[14px] font-bold text-[#1F1F1F]">Your draft appears here</div>
                                <p className="text-[12px] mt-1 max-w-sm" style={{ color: "#6F6A64" }}>Pick a report type, add your notes and click Generate.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            <style>{`.ai-fi{width:100%;padding:8px 11px;background:#fff;border:1px solid #E1E8F2;border-radius:8px;font-size:12.5px;color:#1F1F1F;outline:none;}.ai-fi:focus{border-color:#1F6FE5;box-shadow:0 0 0 3px #E3EEFD;}`}</style>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>{label} {required ? <span className="text-[#B33A2E]">*</span> : null}</span>
            {children}
        </label>
    );
}
